import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('coachylb_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  // Tracks whether the initial token-check has completed
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('coachylb_token');
    if (!token) {
      setInitialized(true);
      return;
    }

    // Refresh profile from server so we always have the latest role/coach_id etc.
    authAPI.getProfile()
      .then(res => {
        const u = res.data.user;
        setUser(u);
        localStorage.setItem('coachylb_user', JSON.stringify(u));
      })
      .catch(err => {
        const status = err.response?.status;
        // Only wipe credentials on a real auth failure (401 = token invalid/expired)
        // Do NOT log out on 429 (rate limit), 500, or network errors — those are temporary
        if (status === 401) {
          localStorage.removeItem('coachylb_token');
          localStorage.removeItem('coachylb_user');
          setUser(null);
        }
        // For all other errors the user object from localStorage stays intact
        // so the UI continues to work with cached data
      })
      .finally(() => {
        setInitialized(true);
      });
  }, []); // eslint-disable-line

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await authAPI.login(email, password);
      const { token, user: userData } = res.data;
      setUser(userData);
      localStorage.setItem('coachylb_user', JSON.stringify(userData));
      localStorage.setItem('coachylb_token', token);
      setLoading(false);
      return { success: true, user: userData };
    } catch (err) {
      setLoading(false);
      return { success: false, message: err.response?.data?.message || 'Login failed' };
    }
  }, []);

  const register = useCallback(async (data) => {
    setLoading(true);
    try {
      const res = await authAPI.register(data);
      const { token, user: userData } = res.data;
      setUser(userData);
      localStorage.setItem('coachylb_user', JSON.stringify(userData));
      localStorage.setItem('coachylb_token', token);
      setLoading(false);
      return { success: true, user: userData };
    } catch (err) {
      setLoading(false);
      return { success: false, message: err.response?.data?.message || 'Registration failed' };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('coachylb_user');
    localStorage.removeItem('coachylb_token');
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const res = await authAPI.forgotPassword(email);
      return { success: true, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Request failed' };
    }
  }, []);

  const updateProfile = useCallback(async (data) => {
    try {
      const res = await authAPI.updateProfile(data);
      const updated = res.data.user;
      setUser(updated);
      localStorage.setItem('coachylb_user', JSON.stringify(updated));
      return { success: true, user: updated };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Update failed' };
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, initialized, login, register, logout, forgotPassword, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
